import type { SelectSidePanelTabAction } from 'models/analytics';
import { connect } from 'react-redux';
import type { Category } from 'shared/middlewares/analytics/logger';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { formProductContainsAssetType, formProductSubtypeIs } from 'state/common-form/commonForm.selectors';
import { getQuoteType, isLoadingPreviousQuote } from 'state/quotes/selectors';
import { isPastQuote, isQuoteTypeWithRegulation } from 'state/quotes/utils';
import {
  getActiveTabInSidePanel,
  getFormInfos,
  getFormQuoteState,
  getFormRequestType,
  getFormWorkflowStatus,
  getFormWorkflowStatusQuoted,
  getIsUserInternal,
  hasAdvisory,
} from 'state/selectors';
import { getQuoteExternalIdByWorkspaceTab } from 'state/selectors/common/temp';
import { hasFees, hasGreeks, hasMetrics, isForBooking } from 'state/selectors/meta/quote/utils';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { changeTabInSidePanelThunk } from 'state/uiPanelState/sidePanelUi/sidePanel.thunk';
import { SidePanelTabId } from 'state/uiPanelState/sidePanelUi/sidePanelUi.model';

export interface OwnProps {
  tabId: string;
  workspaceType: 'static' | 'dynamic';
}

export interface StateProps {
  activeTab: SidePanelTabId;
  displayAdvisoryTab: boolean;
  hasRegulation: boolean;
  hasGreeks: boolean;
  hasFees: boolean;
  hasMetrics: boolean;
  hasMissedDealFeedback: boolean;
  hasBackTest: boolean;
  quoteId?: string;
  isNotReady: boolean;
  isForBooking: boolean;
}

export type SidePanelProps = StateProps & OwnProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId, workspaceType }: OwnProps): StateProps => {
  const formInfos = getFormInfos(state, workspaceType, tabId);
  const quoteState = getFormQuoteState(state, workspaceType, tabId);
  const quoteId = formInfos?.quoteId;
  const quoteType = getQuoteType(state, quoteId);
  const requestType = getFormRequestType(state, workspaceType, tabId);
  const ready = !!formInfos;
  const currentWorkflow = getFormWorkflowStatus(state, workspaceType, tabId);
  const isQuoted = getFormWorkflowStatusQuoted(state, workspaceType, tabId);

  const externalQuoteId = getQuoteExternalIdByWorkspaceTab(state, workspaceType, tabId);
  const hasExternalQuoteId = externalQuoteId !== undefined;

  const hasPreviousRegulation = isPastQuote(quoteState);

  const isRegulationQuote = quoteType !== undefined && isQuoteTypeWithRegulation(quoteType);

  const displayAdvisoryTab = !!quoteId && hasAdvisory(state, quoteId, currentWorkflow) && quoteState !== 'Inconsistent';

  const activeTab = getActiveTabInSidePanel(state, tabId);

  const hasRegulation =
    requestType === 'RequestForStream' ? isRegulationQuote : isRegulationQuote || hasPreviousRegulation;

  const hasMissedDealFeedback =
    isQuoted && hasExternalQuoteId && getIsUserInternal(state) && quoteState !== 'Inconsistent';

  const isLoadingPrevious = isLoadingPreviousQuote(state, quoteId);

  const isEquityProduct = formProductContainsAssetType(state, tabId, workspaceType, 'Equity');
  const hasBackTest =
    !!hasExternalQuoteId &&
    isQuoted &&
    isEquityProduct &&
    !formProductSubtypeIs(state, tabId, 'KIKO') &&
    quoteState !== 'Inconsistent';

  return {
    activeTab,
    displayAdvisoryTab,
    hasGreeks: hasGreeks(state, workspaceType, tabId),
    hasMetrics: hasMetrics(state, workspaceType, tabId),
    hasFees: hasFees(state, workspaceType, tabId),
    hasBackTest,
    hasRegulation,
    hasMissedDealFeedback,
    quoteId,
    isNotReady: !ready || isLoadingPrevious,
    isForBooking: isForBooking(state, quoteId),
  };
};

interface DispatchProps {
  changeTabInSidePanel: (activeTab: SidePanelTabId) => void;
}

const mapDispatchToProps = (dispatch: ThunkDispatch, { workspaceType, tabId }: OwnProps): DispatchProps => {
  return {
    changeTabInSidePanel: (activeTab: SidePanelTabId) => {
      dispatch(changeTabInSidePanelThunk(workspaceType, tabId, activeTab));

      const analyticCategory: Category =
        workspaceType === 'static' ? 'Primary market static' : 'Primary market dynamic';

      const analyticAction = analyticSelectTabAction(activeTab);

      if (analyticAction) {
        logAnalytics.trackEvent(analyticCategory, analyticAction);
      }
    },
  };
};

function analyticSelectTabAction(activeTab: SidePanelTabId): SelectSidePanelTabAction | undefined {
  switch (activeTab) {
    case 'quotation':
      return 'Select Quotation tab';
    case 'advisory':
      return 'Select Advisory tab';
    case 'regulatory':
      return 'Select Regulation tab';
    case 'missedDealFeedback':
      return 'Select Missed Deal Feedback tab';
    case 'backTest':
      return 'Select Backtest tab';
    default:
      return undefined;
  }
}

export const connectSidePanel = connect(mapStateToProps, mapDispatchToProps);
